/**
 * Item Library Integration for Retaining Walls
 * Maps field names to rates from Item Library API
 * Handles both high-level wall types and detailed build-up items
 */

import { ItemLibraryResponse, ItemLibraryItem } from '../api/itemLibrary';
import { RETAINING_WALLS_REFERENCES, resolveScreenWallsCode, resolveEOPillarCode } from './retainingWallsReferenceMapping';
import { RETAINING_WALLS_FIELD_LABELS, RETAINING_WALLS_FIELD_UNITS } from './retainingWallsFieldMapping';

export interface RateInfo {
  rate: number;
  description: string;
  unit: string;
}

/**
 * Get rate from item library by resource code
 */
function getRateByCode(
  resourceCode: string,
  resourceCodeMap: Map<string, ItemLibraryItem>,
  fieldName: string,
  fallbackDescription?: string,
): RateInfo {
  const normalizedCode = resourceCode.trim().toUpperCase();
  const item = resourceCodeMap.get(normalizedCode);

  if (item) {
    return {
      rate: item.nettRate || item.baseRate || 0,
      description: item.resourceDescription || fallbackDescription || fieldName,
      unit: item.unit || 'm',
    };
  }

  // Item not found - use fallback description if provided
  return {
    rate: 0,
    description: fallbackDescription || fieldName,
    unit: 'm',
  };
}

/**
 * Create a rate lookup map from item library for Retaining Walls
 * @param itemLibraryData - Item library response from API
 */
export function createRetainingWallsRateLookupMap(
  itemLibraryData: ItemLibraryResponse | undefined,
  screenWallsLabel?: string,
  eoPillarLabel?: string,
): Map<string, RateInfo> {
  const rateMap = new Map<string, RateInfo>();

  if (!itemLibraryData) {
    return rateMap;
  }

  // Create a map of resourceCode to item for quick lookup
  const resourceCodeMap = new Map<string, ItemLibraryItem>();
  itemLibraryData.forEach((item: ItemLibraryItem) => {
    const code = item.resourceCode?.trim().toUpperCase();
    if (code) {
      resourceCodeMap.set(code, item);
    }
  });

  // Map each field name to its rate info (rate, description, unit)
  Object.entries(RETAINING_WALLS_REFERENCES).forEach(([fieldName, reference]) => {
    // Get proper label from field mapping as fallback
    const fallbackDescription = RETAINING_WALLS_FIELD_LABELS[fieldName] || fieldName;

    // Screen Walls / EO Pillar — each code depends on its own independent
    // Free Issue/M&J Supply Configure toggle rather than a fixed code.
    if (fieldName === 'screenWalls' || fieldName === 'screenWallsEO') {
      const resolvedCode = fieldName === 'screenWalls'
        ? resolveScreenWallsCode(screenWallsLabel)
        : resolveEOPillarCode(eoPillarLabel);
      const rateInfo = getRateByCode(resolvedCode, resourceCodeMap, fieldName, fallbackDescription);
      rateMap.set(fieldName, { ...rateInfo, unit: fieldName in RETAINING_WALLS_FIELD_UNITS ? RETAINING_WALLS_FIELD_UNITS[fieldName] : rateInfo.unit });
      return;
    }

    if (!reference.code) {
      // Fields without codes (high-level wall types that are calculated)
      rateMap.set(fieldName, {
        rate: 0,
        description: fallbackDescription,
        unit: RETAINING_WALLS_FIELD_UNITS[fieldName] ?? '',
      });
      return;
    }

    // Static code from reference mapping
    const rateInfo = getRateByCode(reference.code, resourceCodeMap, fieldName, fallbackDescription);
    rateMap.set(fieldName, { ...rateInfo, unit: fieldName in RETAINING_WALLS_FIELD_UNITS ? RETAINING_WALLS_FIELD_UNITS[fieldName] : rateInfo.unit });
  });

  return rateMap;
}

/**
 * Get rate info for a specific field name
 */
export function getRetainingWallsRateInfo(
  fieldName: string,
  rateLookupMap: Map<string, RateInfo>,
): RateInfo {
  return rateLookupMap.get(fieldName) || { rate: 0, description: fieldName, unit: RETAINING_WALLS_FIELD_UNITS[fieldName] ?? '' };
}

/**
 * Apply rates and descriptions to Retaining Walls summary items
 */
export function applyRetainingWallsRatesToSummary(
  summaryItems: any[],
  rateLookupMap: Map<string, RateInfo>,
): any[] {
  return summaryItems.map((item) => {
    const rateInfo = getRetainingWallsRateInfo(item.fieldName, rateLookupMap);
    const amount = Math.abs(item.quantity * rateInfo.rate);

    return {
      ...item,
      rate: rateInfo.rate,
      description: item.description,
      unit: rateInfo.unit,
      amount,
    };
  });
}
